// apps/web/src/features/clients/useClientSubmitError.ts
//
// Turns a failed client-aggregate write into what the editor must show: a
// localized, actionable in-form message, a field error on the input that caused
// it, and — for a request that never got a verdict — the fact that the outcome
// is UNKNOWN (AC-9).
//
// INVARIANT: the form keeps the operator's input in every branch. Nothing here
// closes the editor or clears fields; a failed save must never cost typed work.
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../api/errors.js';
import {
  clientConflictMessageKey,
  isUncertainOutcome,
  parseClientConflict,
  type ClientDuplicateField,
} from './clientDomainErrors.js';

export interface ClientSubmitErrorState {
  /** Localized in-form message; `null` while there is nothing to report. */
  text: string | null;
  /**
   * The write may or may not have been applied. Callers must not resubmit the
   * same POST — they offer a refresh-and-check path instead.
   */
  uncertain: boolean;
  /**
   * True once the operator has actually looked at the refreshed server state
   * behind an uncertain outcome. Submitting again is then a considered act, not
   * a blind replay, so callers stop blocking it.
   */
  inspected: boolean;
}

export interface ClientSubmitErrorOptions {
  /**
   * The request addresses an EXISTING row by id (PATCH/archive/restore), so
   * repeating it cannot create a second record. A missing verdict is then an
   * ordinary retryable failure, not an unresolved outcome.
   */
  idempotent?: boolean;
}

const CLEAR: ClientSubmitErrorState = { text: null, uncertain: false, inspected: false };

/**
 * @aiPurpose shared failure handling for the client workspace editors.
 * @aiInvariant an uncertain (no-verdict) outcome is reported as such so no
 *   caller replays a create blindly and duplicates the record.
 */
export function useClientSubmitError() {
  const { t } = useTranslation(['clients', 'common']);
  const [state, setState] = useState<ClientSubmitErrorState>(CLEAR);

  const clear = useCallback(() => setState(CLEAR), []);

  const report = useCallback(
    (
      err: unknown,
      applyFieldError?: (field: ClientDuplicateField, message: string) => void,
      options: ClientSubmitErrorOptions = {},
    ) => {
      if (isUncertainOutcome(err)) {
        // A known-id write is addressed at one row: repeating it converges on
        // the same state, so it stays an ordinary retry instead of a block.
        if (options.idempotent) {
          setState({
            text: t('clients:conflict.noVerdictRetry'),
            uncertain: false,
            inspected: false,
          });
          return;
        }
        setState({
          text: t('clients:conflict.uncertainOutcome'),
          uncertain: true,
          inspected: false,
        });
        return;
      }
      const conflict = parseClientConflict(err);
      if (conflict === null) {
        setState({ text: errorMessage(err), uncertain: false, inspected: false });
        return;
      }
      const text = t(clientConflictMessageKey(conflict));
      if (conflict.kind === 'duplicate-field' && applyFieldError) {
        // The message belongs next to the offending input; the banner repeats
        // it so a screen reader announces the failure without moving focus.
        applyFieldError(conflict.field, text);
      }
      setState({ text, uncertain: false, inspected: false });
    },
    [t],
  );

  /**
   * Record that the operator inspected the refreshed server state. Keeps the
   * uncertainty visible (the outcome is still unknown to US) but stops treating
   * a further submit as a blind replay.
   */
  const markInspected = useCallback(() => {
    setState((prev) =>
      prev.uncertain
        ? { ...prev, text: t('clients:conflict.uncertainInspected'), inspected: true }
        : prev,
    );
  }, [t]);

  return { state, report, clear, markInspected };
}
